import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import SignupHero from "../../Images/Login/SignupHeroCutout.png";
import CheckIcon from "@mui/icons-material/Check";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"; // was Step 4 (Plan) selected-marker icon
import { useThemeContext } from "../../contexts/ThemeContext";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import { MuiTelInput } from "mui-tel-input";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const regionNames =
  typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

function countryNameFromCode(code) {
  if (!code || !regionNames) return null;
  try {
    return regionNames.of(code);
  } catch {
    return null;
  }
}

const DEFAULT_PHONE_COUNTRY = "US";

function isValidWebsite(raw) {
  const trimmed = raw.trim();
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const { hostname } = new URL(candidate);
    return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(hostname);
  } catch {
    return false;
  }
}

const businessTypes = [
  "E-commerce / Retail",
  "Healthcare",
  "Education",
  "Financial Services",
  "Real Estate",
  "Travel & Hospitality",
  "Technology / SaaS",
  "Media & Entertainment",
  "Food & Beverage",
  "Logistics & Supply Chain",
  "Non-Profit",
  "Other",
];

const employeeSizes = ["2-10", "10-50", "50-100", "100+"];

const STEPS = [
  { num: 1, label: "Organization" },
  { num: 2, label: "Business" },
  { num: 3, label: "Personal" },
  // { num: 4, label: "Plan" }, // commented out — App Store 1.2.1 billing-compliance fix, no in-app plan picker
];

const stepEnterForward = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const stepEnterBack = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const STEP_CONTENT = {
  1: { title: "Organization", subtitle: "Tell us about the organization you're setting up." },
  2: { title: "Business Profile", subtitle: "Help us tailor your workspace to your industry." },
  3: { title: "Personal Information", subtitle: "So we know who's setting up this workspace." },
  // 4: { title: "Choose Your Plan", subtitle: "You can change this anytime later." }, // commented out with Step 4
};

// Same responsive fixes as Login.jsx/Signup.jsx (see AUTH_COMPACT_HEIGHT_MQ in Login.jsx
// for the full rationale).
const AUTH_COMPACT_HEIGHT_MQ = "@media (max-height:800px)";

const Setup = () => {
  const [formStep, setFormStep] = useState(1);
  const [stepDirection, setStepDirection] = useState("forward");
  const prevStepRef = useRef(1);

  useEffect(() => {
    setStepDirection(formStep >= prevStepRef.current ? "forward" : "back");
    prevStepRef.current = formStep;
  }, [formStep]);
  const [profile, setProfile] = useState({
    orgName: "",
    companyWebsite: "",
    businessType: "",
    otherBusiness: "",
    employeeSize: "",
    ownerName: "",
    phone: "",
    country: countryNameFromCode(DEFAULT_PHONE_COUNTRY) || "",
  });
  // const [selectedPlan, setSelectedPlan] = useState("trial"); // Step 4 (Plan) commented out
  // const [plans, setPlans] = useState([]);
  // const [plansLoading, setPlansLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const accountId = useSelector((state) => state.auth.user?.accountId);

  // Step 4 (Plan) plan-list fetch — commented out along with the plan picker itself.
  // useEffect(() => {
  //   let cancelled = false;
  //   axios
  //     .get(`${SERVER_DOMAIN}/api/subscriptions/plans/public`)
  //     .then((res) => {
  //       if (!cancelled && res.data?.success) {
  //         setPlans(res.data.plans || []);
  //       }
  //     })
  //     .catch(() => {})
  //     .finally(() => {
  //       if (!cancelled) setPlansLoading(false);
  //     });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, []);

  const validateAndNormalizePhone = (raw) => {
    if (!raw || typeof raw !== "string") return { ok: false, message: "Phone is required" };
    const cleaned = raw.replace(/[\s\-()]/g, "").trim();
    const candidate = cleaned.startsWith("+")
      ? cleaned
      : cleaned.startsWith("00")
      ? "+" + cleaned.slice(2)
      : cleaned.match(/^\d+$/)
      ? "+" + cleaned
      : cleaned;

    if (!isValidPhoneNumber(candidate)) {
      return { ok: false, message: "Enter a valid phone number in international format" };
    }
    const parsed = parsePhoneNumberFromString(candidate);
    if (!parsed || !parsed.isValid()) {
      return { ok: false, message: "Enter a valid phone number" };
    }
    return { ok: true };
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!profile.orgName?.trim()) newErrors.orgName = "Organization name is required";
    if (profile.companyWebsite?.trim() && !isValidWebsite(profile.companyWebsite)) {
      newErrors.companyWebsite = "Enter a valid website URL";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!profile.businessType) newErrors.businessType = "Please select a business type";
    if (profile.businessType === "Other" && !profile.otherBusiness?.trim()) {
      newErrors.otherBusiness = "Please describe your business";
    }
    if (!profile.employeeSize) newErrors.employeeSize = "Please select your team size";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!profile.ownerName?.trim()) newErrors.ownerName = "Full name is required";
    if (!profile.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      const res = validateAndNormalizePhone(profile.phone);
      if (!res.ok) newErrors.phone = res.message;
    }
    if (!profile.country) newErrors.country = "Country is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const response = await axios.patch(`${SERVER_DOMAIN}/api/accounts/complete-onboarding`, {
        orgName: profile.orgName.trim(),
        companyWebsite: profile.companyWebsite?.trim() || "",
        businessType: profile.businessType,
        otherBusiness: profile.otherBusiness?.trim() || "",
        employeeSize: profile.employeeSize,
        ownerName: profile.ownerName.trim(),
        phone: profile.phone.trim(),
        country: profile.country,
        // plan: selectedPlan, // Step 4 (Plan) commented out — completeOnboarding's `if (plan)` block is a harmless no-op now
      });
      if (response.data?.success) {
        navigate(`/home/${accountId}/onboard`, { replace: true });
      } else {
        throw new Error(response.data?.message || "Unable to complete setup.");
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) setErrors((prev) => ({ ...prev, ...data.errors }));
      setSubmitError(data?.message || err.message || "Unable to complete setup right now.");
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (formStep === 1 && !validateStep1()) return;
    if (formStep === 2 && !validateStep2()) return;
    if (formStep === 3 && !validateStep3()) return;

    // Was `formStep < 4` while Step 4 (Plan) was active — left as an actual value change
    // (not commented out) since Step 4's JSX is now commented out too; keeping this at 4
    // would advance to an empty step instead of submitting.
    if (formStep < 3) {
      setFormStep(formStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (formStep > 1) {
      setFormStep(formStep - 1);
      setErrors({});
    }
  };

  const getFieldStyles = () => {
    if (mode === "dark") {
      return {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#141414",
          borderRadius: "12px",
          transition: "all 0.3s ease",
          "& fieldset": { borderColor: "#333333", borderWidth: "1.5px" },
          "&:hover": { backgroundColor: "#1c1c1c", "& fieldset": { borderColor: "#4a4a4a" } },
          "&.Mui-focused": { backgroundColor: "#1c1c1c", "& fieldset": { borderColor: "#34d399", borderWidth: "2px" } },
        },
        "& .MuiInputBase-input": {
          color: "#f5f5f5",
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "17px",
          padding: "14px 16px",
        },
        "& .MuiSelect-select": { display: "flex", alignItems: "center", minHeight: "1.4375em" },
        "& .MuiSvgIcon-root": { color: "#6b7280" },
        "& .MuiFormHelperText-root": { color: "#9ca3af", "&.Mui-error": { color: "#ef4444" } },
      };
    }
    return {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F3F4F6",
        borderRadius: "12px",
        "& fieldset": { borderColor: "#E5E7EB" },
        "&:hover fieldset": { borderColor: "#D1D5DB" },
        "&.Mui-focused fieldset": { borderColor: "#a0eaa3", borderWidth: "2px" },
      },
      "& .MuiInputBase-input": {
        color: "#111827",
        fontFamily: "Instrument Sans, sans-serif",
        fontSize: "17px",
        padding: "14px 16px",
      },
      "& .MuiSelect-select": { display: "flex", alignItems: "center", minHeight: "1.4375em" },
    };
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.75,
        borderRadius: "12px",
        backgroundColor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
        color: mode === "dark" ? "#f5f5f5" : "#111827",
        border: mode === "dark" ? "1px solid #333333" : "1px solid #E5E7EB",
        boxShadow: mode === "dark" ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(17,24,39,0.1)",
        maxHeight: 320,
        scrollbarWidth: "thin",
        scrollbarColor:
          mode === "dark" ? "rgba(255, 255, 255, 0.22) transparent" : "rgba(15, 23, 42, 0.18) transparent",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 8,
          backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.22)" : "rgba(15, 23, 42, 0.18)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.34)" : "rgba(15, 23, 42, 0.28)",
        },
        "& .MuiMenuItem-root": {
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "15px",
          borderRadius: "8px",
          "&:hover": { backgroundColor: mode === "dark" ? "#2a2a2a" : "#F3F4F6" },
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "#10b981" : "#D1FAE5",
            color: mode === "dark" ? "#000" : "#111827",
          },
        },
      },
    },
  };

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  const fieldLabelSx = {
    mb: 1,
    fontWeight: 600,
    fontSize: "13.5px",
    color: mode === "dark" ? "#d1d5db" : "#374151",
    fontFamily: "Instrument Sans, sans-serif",
  };
  const sectionHeadingSx = {
    mb: 0,
    lineHeight: 1.2,
    fontWeight: 700,
    fontSize: { xs: "1.85rem", sm: "2.1rem" },
    color: mode === "dark" ? "#f5f5f5" : "#121a34",
    fontFamily: "Instrument Sans, sans-serif",
    [AUTH_COMPACT_HEIGHT_MQ]: { fontSize: { xs: "1.5rem", sm: "1.65rem" } },
  };
  const sectionSubheadingSx = {
    mt: 0.5,
    mb: 3,
    fontSize: "13.5px",
    color: mode === "dark" ? "#9ca3af" : "#6b7280",
    fontFamily: "Instrument Sans, sans-serif",
    [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 },
  };

  if (submitting) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: mode === "dark" ? "#121212" : "#FFFFFF",
          gap: 3,
        }}
      >
        <CircularProgress size={56} thickness={3} sx={{ color: accentColor }} />
        <Typography
          sx={{
            fontFamily: "Instrument Sans, sans-serif",
            fontWeight: 700,
            fontSize: "1.3rem",
            color: mode === "dark" ? "#f5f5f5" : "#121a34",
          }}
        >
          Finishing your setup…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        background: mode === "dark" ? "#121212" : "#FAFBFC",
        fontFamily: "Instrument Sans, sans-serif",
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        // Caps the two-panel layout on genuinely ultrawide monitors only — see Login.jsx's
        // identical rule for why this uses an explicit min-width instead of MUI's "xl"
        // breakpoint (1536px is too low; 1920px is a completely standard monitor width).
        maxWidth: "none",
        "@media (min-width:2200px)": { maxWidth: "1800px" },
        mx: "auto",
      }}
    >
      {/* Left branding card — copied verbatim from Signup.jsx (same layout/structure across all auth pages) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 39%",
          flexDirection: "column",
          m: { md: 3 },
          p: { md: 5 },
          pl: { md: 5, lg: 5.5 },
          borderRadius: 5,
          overflow: "hidden",
          backgroundColor: mode === "dark" ? "#1E1E1E" : "#FAFBFC",
          border: mode === "dark" ? "none" : "2px solid rgba(0,0,0,0.1)",
          color: mode === "dark" ? "#f5f5f5" : "#000000",
          position: "relative",
          [AUTH_COMPACT_HEIGHT_MQ]: { m: 1.5, p: 3, pl: { md: 4, lg: 4.5 } },
        }}
      >
        <Box
          component="img"
          src={SignupHero}
          alt=""
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "82%",
            height: "82%",
            [AUTH_COMPACT_HEIGHT_MQ]: { width: "90%", height: "90%" },
            objectFit: "contain",
            objectPosition: "left bottom",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 5,
            position: "relative",
            zIndex: 1,
            [AUTH_COMPACT_HEIGHT_MQ]: { mb: 2 },
          }}
        >
          <Box
            component="img"
            src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
            alt="ClusterDen"
            sx={{
              height: 44,
              width: "auto",
              maxWidth: 200,
              objectFit: "contain",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              onClick={() => window.open("https://wa.me/923250909111", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Help
            </Button>
            <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
            <Button
              onClick={() => window.open("https://clusterden.com/privacy", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Privacy
            </Button>
            <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
            <Button
              onClick={() => window.open("https://clusterden.com/terms", "_blank", "noopener,noreferrer")}
              sx={{
                p: 0,
                minWidth: "auto",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: mode === "dark" ? "#9ca3af" : "#6B7280",
                fontFamily: "Instrument Sans, sans-serif",
                "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
              }}
            >
              Terms
            </Button>
          </Box>
        </Box>

        {/* Headline */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 500,
            fontSize: { md: "1.95rem", lg: "2.25rem" },
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            mb: 2,
            whiteSpace: "nowrap",
            color: mode === "dark" ? "#f5f5f5" : "#000000",
            position: "relative",
            zIndex: 1,
          }}
        >
          You&apos;re almost there
        </Typography>
        <Typography
          sx={{
            fontSize: { md: "0.95rem", lg: "1.05rem" },
            lineHeight: 1.6,
            color: "#9CA3AF",
            whiteSpace: "nowrap",
            position: "relative",
            zIndex: 1,
          }}
        >
          Just a few details left to set up your workspace.
        </Typography>
      </Box>

      {/* Right side — form sits directly on the page background, no tiles/card */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          // "safe center" so growing content never pushes part of the form above the
          // visible/scrollable area — see Login.jsx's identical rule for the full explanation.
          justifyContent: "safe center",
          boxSizing: "border-box",
          padding: { xs: 2.5, sm: 3, md: 4 },
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: 520,
              boxSizing: "border-box",
              mx: "auto",
              py: { xs: 3.5, sm: 4.5, md: 5 },
              px: { xs: 3, sm: 4.5, md: 5 },
              [AUTH_COMPACT_HEIGHT_MQ]: { py: 2 },
            }}
          >
            {/* Header row: step heading/subheading left, step-dot indicator right — parallel, same row (logo + "Welcome to ClusterDen" removed) */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 3, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 } }}>
              <Box
                key={formStep}
                sx={{
                  animation: `${stepDirection === "forward" ? stepEnterForward : stepEnterBack} 0.32s ease`,
                }}
              >
                <Typography sx={sectionHeadingSx}>{STEP_CONTENT[formStep].title}</Typography>
                <Typography sx={sectionSubheadingSx}>{STEP_CONTENT[formStep].subtitle}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, flexShrink: 0 }}>
                {STEPS.map((step) => (
                  <Box
                    key={step.num}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        formStep >= step.num ? accentColor : mode === "dark" ? "#333333" : "#E5E7EB",
                      transition: "all 0.25s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box component="form" noValidate>
              <Box
                key={formStep}
                sx={{
                  minHeight: { xs: "auto", sm: 220 },
                  animation: `${stepDirection === "forward" ? stepEnterForward : stepEnterBack} 0.32s ease`,
                  [AUTH_COMPACT_HEIGHT_MQ]: { minHeight: { xs: "auto", sm: 140 } },
                }}
              >
                {formStep === 1 && (
                  <>
                    <Typography sx={fieldLabelSx}>Organization Name</Typography>
                    <TextField
                      fullWidth
                      name="orgName"
                      placeholder="Acme Corp"
                      value={profile.orgName}
                      onChange={handleProfileChange}
                      error={!!errors.orgName}
                      helperText={errors.orgName}
                      sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }}
                    />

                    <Typography sx={fieldLabelSx}>Company Website</Typography>
                    <TextField
                      fullWidth
                      name="companyWebsite"
                      placeholder="https://yourcompany.com"
                      value={profile.companyWebsite}
                      onChange={handleProfileChange}
                      error={!!errors.companyWebsite}
                      helperText={errors.companyWebsite}
                      sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }}
                    />
                  </>
                )}

                {formStep === 2 && (
                  <>
                    <Typography sx={fieldLabelSx}>Business Type</Typography>
                    <FormControl fullWidth sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }} error={!!errors.businessType}>
                      <Select
                        displayEmpty
                        name="businessType"
                        value={profile.businessType}
                        onChange={handleProfileChange}
                        MenuProps={selectMenuProps}
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <Box component="span" sx={{ color: mode === "dark" ? "#6b7280" : "#9CA3AF" }}>
                              Select business type
                            </Box>
                          )
                        }
                      >
                        {businessTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                      {errors.businessType && (
                        <Typography variant="caption" sx={{ color: mode === "dark" ? "#f87171" : "#EF4444", mt: 0.5, ml: 1.75, fontSize: "0.75rem" }}>
                          {errors.businessType}
                        </Typography>
                      )}
                    </FormControl>

                    {profile.businessType === "Other" && (
                      <>
                        <Typography sx={fieldLabelSx}>Describe your business</Typography>
                        <TextField
                          fullWidth
                          name="otherBusiness"
                          placeholder="Tell us what you do"
                          value={profile.otherBusiness}
                          onChange={handleProfileChange}
                          error={!!errors.otherBusiness}
                          helperText={errors.otherBusiness}
                          sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }}
                        />
                      </>
                    )}

                    <Typography sx={fieldLabelSx}>How many employees?</Typography>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 0.5 }}>
                      {employeeSizes.map((size) => {
                        const selected = profile.employeeSize === size;
                        return (
                          <Box
                            key={size}
                            onClick={() => {
                              setProfile((prev) => ({ ...prev, employeeSize: size }));
                              setErrors((prev) => ({ ...prev, employeeSize: "" }));
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              px: 2,
                              py: 1,
                              borderRadius: "10px",
                              border: `1.5px solid ${selected ? accentColor : mode === "dark" ? "#333333" : "#E5E7EB"}`,
                              backgroundColor: selected
                                ? mode === "dark" ? "rgba(79,195,137,0.12)" : "rgba(68,181,124,0.08)"
                                : mode === "dark" ? "#1E1E1E" : "#ffffff",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              userSelect: "none",
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                border: `1.5px solid ${selected ? accentColor : mode === "dark" ? "#4a4a4a" : "#D1D5DB"}`,
                                backgroundColor: selected ? accentColor : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {selected && <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />}
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: "Instrument Sans, sans-serif",
                                fontSize: "13px",
                                fontWeight: selected ? 600 : 400,
                                color: selected ? accentColor : mode === "dark" ? "#d1d5db" : "#374151",
                              }}
                            >
                              {size}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {errors.employeeSize && (
                      <Typography variant="caption" sx={{ color: mode === "dark" ? "#f87171" : "#EF4444", mt: 0.5, fontSize: "0.75rem", display: "block" }}>
                        {errors.employeeSize}
                      </Typography>
                    )}
                  </>
                )}

                {formStep === 3 && (
                  <>
                    <Typography sx={fieldLabelSx}>Full Name</Typography>
                    <TextField
                      fullWidth
                      name="ownerName"
                      placeholder="John Doe"
                      value={profile.ownerName}
                      onChange={handleProfileChange}
                      error={!!errors.ownerName}
                      helperText={errors.ownerName}
                      sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }}
                    />

                    <Typography sx={fieldLabelSx}>Phone Number</Typography>
                    <MuiTelInput
                      fullWidth
                      defaultCountry={DEFAULT_PHONE_COUNTRY}
                      value={profile.phone}
                      onChange={(value, info) => {
                        setProfile((prev) => ({
                          ...prev,
                          phone: value || "",
                          country: info.countryCode ? countryNameFromCode(info.countryCode) || prev.country : prev.country,
                        }));
                        setErrors((prev) => ({ ...prev, phone: "", country: "" }));
                      }}
                      error={!!errors.phone || !!errors.country}
                      helperText={errors.phone || errors.country || "Pick your country flag, then enter your number"}
                      MenuProps={selectMenuProps}
                      sx={{ mb: 2.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.5 }, ...getFieldStyles() }}
                    />
                  </>
                )}

                {/* Step 4 (Plan) — commented out, App Store 1.2.1 billing-compliance fix (no in-app plan picker anywhere)
                {formStep === 4 && (
                  <>
                    {plansLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} sx={{ color: accentColor }} />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 1 }}>
                        {plans.map((plan) => {
                          const selected = selectedPlan === plan.code;
                          const priceLabel =
                            plan.code === "trial" || plan.code === "custom"
                              ? plan.code === "trial" ? "Free" : "Contact us"
                              : plan.monthlyPricePkr
                              ? `PKR ${plan.monthlyPricePkr.toLocaleString()}/mo`
                              : "";
                          return (
                            <Box
                              key={plan.code}
                              onClick={() => setSelectedPlan(plan.code)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2.5,
                                py: 1.75,
                                borderRadius: "12px",
                                border: `1.5px solid ${selected ? accentColor : mode === "dark" ? "#333333" : "#E5E7EB"}`,
                                backgroundColor: selected
                                  ? mode === "dark" ? "rgba(79,195,137,0.1)" : "rgba(68,181,124,0.06)"
                                  : mode === "dark" ? "#1E1E1E" : "#ffffff",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                {selected ? (
                                  <CheckCircleOutlineIcon sx={{ fontSize: 20, color: accentColor }} />
                                ) : (
                                  <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: `1.5px solid ${mode === "dark" ? "#4a4a4a" : "#D1D5DB"}` }} />
                                )}
                                <Typography sx={{ fontFamily: "Instrument Sans, sans-serif", fontWeight: 600, fontSize: "14px", color: mode === "dark" ? "#f5f5f5" : "#121a34" }}>
                                  {plan.name}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontFamily: "Instrument Sans, sans-serif", fontSize: "13px", fontWeight: 500, color: mode === "dark" ? "#9ca3af" : "#6b7280" }}>
                                {priceLabel}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
                */}
              </Box>

              {submitError && (
                <Typography sx={{ color: mode === "dark" ? "#f87171" : "#EF4444", fontSize: "14px", mb: 2, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1 }, fontFamily: "Instrument Sans, sans-serif" }}>
                  {submitError}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 2, mt: 3, [AUTH_COMPACT_HEIGHT_MQ]: { mt: 1.5 } }}>
                {formStep > 1 && (
                  <Button
                    onClick={handleBack}
                    variant="outlined"
                    fullWidth
                    sx={{
                      py: 1.75,
                      [AUTH_COMPACT_HEIGHT_MQ]: { py: 1.25 },
                      borderColor: mode === "dark" ? "#333333" : "#E1E4E8",
                      color: mode === "dark" ? "#f5f5f5" : "#3C4043",
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      letterSpacing: "0.01em",
                      fontFamily: "Instrument Sans, sans-serif",
                      "&:hover": {
                        borderColor: mode === "dark" ? "#4a4a4a" : "#C1C8CD",
                        backgroundColor: mode === "dark" ? "#1c1c1c" : "#F8F9FA",
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.75,
                    [AUTH_COMPACT_HEIGHT_MQ]: { py: 1.25 },
                    backgroundColor: accentColor,
                    color: "#ffffff",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    letterSpacing: "0.01em",
                    fontFamily: "Instrument Sans, sans-serif",
                    boxShadow: mode === "dark" ? "none" : "0 2px 8px rgba(68,181,124,0.25)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                      boxShadow: mode === "dark" ? "none" : "0 8px 20px rgba(68,181,124,0.35)",
                    },
                  }}
                >
                  {/* Was `formStep === 4` while Step 4 (Plan) was active — same reasoning as handleNext above */}
                  {formStep === 3 ? "Complete Setup" : "Next"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Setup;
